import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Mail, Clock, X } from "lucide-react";
import { OTPInput } from "../../../../components/Inputs";
import {
  sendEmailOtpApi,
  verifyEmailOtpApi,
  editProfile,
} from "../../../../api/authAPI";
import { toast } from "react-toastify";
import Loader from "./Loader";
import { VerifiedBadge } from "./VerifiedBadge";
import {
  formatOtpCountdown,
  getOtpCooldownSeconds,
  getOtpRetryAfterSeconds,
} from "../../../../utils/otpCooldown";

export default function EmailVerification({ isEditing }: any) {
  const {
    watch,
    register,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const email = watch("email");
  const originalEmail = getValues("emailOriginal");
  const emailVerified = watch("emailVerified");

  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // ⏳ Countdown
  useEffect(() => {
    if (!countdown) return;
    const timer = setInterval(() => setCountdown((v) => v - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);
  useEffect(() => {
    const currentEmail = getValues("email");
    const originalEmail = getValues("emailOriginal");

    if (!originalEmail) {
      setValue("emailOriginal", currentEmail);
    }
  }, []);

  // Send OTP on Update Email
  const sendOtp = async () => {
    try {
      setEmailLoading(true);
      const res = await sendEmailOtpApi({ email, purpose: "verify-email" });
      if (res.data.status) {
        toast.success(
          showModal ? "Resent OTP successfully" : "OTP sent to updated email",
        );
        setCountdown(getOtpCooldownSeconds(res.data));
        setShowModal(true);
      } else {
        const retryAfterSeconds = getOtpRetryAfterSeconds(res.data);
        if (retryAfterSeconds) {
          setCountdown(retryAfterSeconds);
        }
        toast.error(res.data.message);
      }
    } catch (error: any) {
      const data = error?.response?.data;
      const retryAfterSeconds = getOtpRetryAfterSeconds(data);
      if (retryAfterSeconds) {
        setCountdown(retryAfterSeconds);
      }
      toast.error(error?.message || data?.message || "Failed to send OTP");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyClick = async () => {
    const isValid = await trigger("email");
    if (!isValid) return; // 🛑 Stop if email is invalid
    sendOtp();
  };

  //  When user clicks UPDATE
  const handleUpdateClick = async () => {
    const isValid = await trigger("email");
    if (!isValid) return;

    sendOtp();
  };

  // ✔ Verify OTP
  const verifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error("Enter 6-digit OTP");
      return;
    }

    try {
      setEmailLoading(true);
      const res = await verifyEmailOtpApi({
        otp,
        email,
      });

      if (!res.data.status) {
        const errorMessage: string =
          res.data?.message || "Invalid OTP or expired";
        toast.error(errorMessage);

        return;
      }

      const updateRes = await editProfile({
        email,
        emailVerifiedUpdate: true, // 🆕 Special flag to skip validation
      });

      // const updateRes = await editProfile({ email,  emailVerifiedUpdate: false });

      if (updateRes.data.status) {
        toast.success("Email verified successfully");
        setValue("emailOriginal", email);
        setValue("emailVerified", true);
        setShowModal(false);
        setOtp("");
        setCountdown(0);
      } else {
        toast.error(updateRes.data.message);
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setEmailLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setOtp("");
    setCountdown(0);
  };

  return (
    <>
      {emailLoading && <Loader />}
      <div className="space-y-3">
        <label className=" text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
          <Mail size={16} />
          Email Address *
        </label>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1">
            <input
              disabled={!isEditing}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/,
                  message: "Please enter a valid email address",
                },
              })}
              className="input md:pr-24"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isEditing && !emailVerified && (
                <button
                  type="button"
                  onClick={() => {
                    handleVerifyClick();
                  }}
                  disabled={countdown > 0}
                  className="px-3 py-1 bg-orange-600 hover:bg-orange-700
               text-white rounded text-sm"
                >
                  {emailLoading ? "Verifying..." : "Verify"}
                </button>
              )}

              {isEditing &&
                emailVerified &&
                email &&
                email !== originalEmail && (
                  <button
                    type="button"
                    onClick={handleUpdateClick}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    Update
                  </button>
                )}
            </div>

            {/* ERROR MESSAGE (HEIGHT RESERVED) */}
            <div>
              {errors?.email?.message && (
                <p className="text-sm text-red-600">
                  {String(errors.email.message)}
                </p>
              )}
            </div>
          </div>

          {/* VERIFIED / UNVERIFIED BADGE */}

          {emailVerified ? (
            <VerifiedBadge verified={emailVerified} />
          ) : (
            <VerifiedBadge verified={emailVerified} />
          )}
        </div>
      </div>

      {/* OTP Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Enter verification code</h3>
              <button onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <p className="">A 6-digit OTP has been sent to:</p>
              <p className="text-center font-medium">{email}</p>

              <div className="mt-5">
                <OTPInput
                  value={otp}
                  length={6}
                  onChange={(v) => setOtp(v)}
                  className="text-black"
                />
              </div>

              <button
                onClick={() => {
                  verifyOtp();
                }}
                className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg"
              >
                Verify OTP
              </button>

              {/* Countdown */}
              <div className="text-center mt-3">
                <button
                  disabled={countdown > 0}
                  onClick={() => {
                    sendOtp();
                  }}
                  className="text-blue-600 text-sm disabled:text-gray-400"
                >
                  {countdown > 0 ? (
                    <span className="flex items-center justify-center gap-1">
                      <Clock size={14} />
                      Resend in {formatOtpCountdown(countdown)}
                    </span>
                  ) : (
                    "Resend Code"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
